import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { Sorter } from '@flakiness/server/common/pagedResponse.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlCheckbox, SlDialog, SlInputEvent } from '@shoelace-style/shoelace';
import { api } from './api.js';
import { docLinkStyles, linkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts, FKProjectChanged } from './contexts.js';
import { renderDate } from './fragments.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { forgeUtils } from './forgeUtils.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { CHECK, humanReadableBytes } from '@flakiness/shared/common/utils.js';
import { tasks } from './tasks.js';

@customElement('page-project-superadm')
export class PageProjectSuperadm extends LitElement {

  static url(options: { orgSlug: string, projectSlug: string, snapshotId: WireTypes.DataSnapshotId | undefined }): string {
    return projectPlatformUrl(['', options.orgSlug, options.projectSlug, 'superadm'].join('/'), undefined, options.snapshotId);
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/superadm',
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-project-superadm
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
        ></page-project-superadm>
      `),
    }];
  }

  @consume({ context: contexts.user }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;

  private _fetchedRepositoryStats = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug, this._snapshotId] as const,
    task: async ([orgSlug, projectSlug, snapshotId], { signal }) => {
      CHECK(orgSlug && projectSlug && snapshotId);
      return await api.project.fetchRepositoryStats.GET({ orgSlug, projectSlug, snapshotId }, { signal });
    },
  });

  private _reportIndexSize = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    task: async ([orgSlug, projectSlug], { signal }) => {
      CHECK(orgSlug && projectSlug);
      return await api.administration.reportIndexSize.GET({ orgSlug, projectSlug }, { signal });
    },
  });

  private _repoSize = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    task: async ([orgSlug, projectSlug], { signal }) => {
      CHECK(orgSlug && projectSlug);
      return await api.administration.repoSize.GET({ orgSlug, projectSlug }, { signal });
    },
  });

  @query('#shards-dialog') private _shardsDialog?: SlDialog;

  private _shardDetails = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    autoRun: false,
    task: async ([orgSlug, projectSlug], { signal }) => {
      CHECK(orgSlug && projectSlug);
      const shards = await api.administration.shardDetails.GET({ orgSlug, projectSlug }, { signal });
      const sorter = new Sorter(shards, {
        id: (s1, s2) => s1.shardId < s2.shardId ? -1 : 1,
        runs: (s1, s2) => s1.runsCount - s2.runsCount,
        commits: (s1, s2) => s1.commitsCount - s2.commitsCount,
        minRun: (s1, s2) => (s1.minRun ?? 0) - (s2.minRun ?? 0),
        maxRun: (s1, s2) => (s1.maxRun ?? 0) - (s2.maxRun ?? 0),
        size: (s1, s2) => s1.sizeBytes - s2.sizeBytes,
        ratio: (s1, s2) => bytesPerRun(s1) - bytesPerRun(s2),
        testResultRatio: (s1, s2) => (bytesPerTestResult(s1) ?? -1) - (bytesPerTestResult(s2) ?? -1),
      });
      sorter.ensureSorted('id', 'desc');
      return sorter;
    },
  });

  private _renderShardsDialog() {
    const tableIcon = (key: 'id' | 'runs' | 'commits' | 'minRun' | 'maxRun' | 'size' | 'ratio' | 'testResultRatio') => {
      const sorter = this._shardDetails.value;
      if (sorter?.axis() !== key)
        return html`<sl-icon></sl-icon>`
      return sorter.direction() === 'desc'
        ? html`<sl-icon name="arrow-down-short"></sl-icon>`
        : html`<sl-icon name="arrow-up-short"></sl-icon>`
      ;
    }

    const toggleSort = (key: 'id' | 'runs' | 'commits' | 'minRun' | 'maxRun' | 'size' | 'ratio' | 'testResultRatio') => {
      const sorter = this._shardDetails.value;
      if (!sorter)
        return;
      if (sorter.axis() === key)
        sorter.ensureSorted(key, sorter.direction() === 'desc' ? 'asc' : 'desc');
      else
        sorter.ensureSorted(key, 'desc');
      this.requestUpdate();
    }

    return html`
      <sl-dialog id="shards-dialog" label="Shard Details">
      ${this._shardDetails.render({
        initial: () => html`<sl-spinner></sl-spinner>`,
        pending: () => html`<sl-spinner></sl-spinner>`,
        error: () => html`<span>Failed to fetch shard details</span>`,
        complete: (shards) => html`
          <section>
            <div class=header @click=${() => toggleSort('id')}>Shard ID ${tableIcon('id')}</div>
            <div class=header @click=${() => toggleSort('runs')}>Runs ${tableIcon('runs')}</div>
            <div class=header>Aggregated</div>
            <div class=header @click=${() => toggleSort('commits')}>Commits ${tableIcon('commits')}</div>
            <div class=header @click=${() => toggleSort('minRun')}>Min Run ${tableIcon('minRun')}</div>
            <div class=header @click=${() => toggleSort('maxRun')}>Max Run ${tableIcon('maxRun')}</div>
            <div class=header @click=${() => toggleSort('size')}>Size ${tableIcon('size')}</div>
            <div class=header @click=${() => toggleSort('ratio')}>MB/run ${tableIcon('ratio')}</div>
            <div class=header @click=${() => toggleSort('testResultRatio')}>B/test result ${tableIcon('testResultRatio')}</div>
            ${shards.all().map(shard => html`
              <div class=cell>
                <a class=shard-id href=${shard.downloadURL}>
                  ${renderDate(parseInt(shard.shardId.split('-')[0], 10))}
                </a>
              </div>
              <div class=cell>${shard.runsCount.toLocaleString()}</div>
              <div class=cell>${shard.builtRunsCount === null ? html`<span class=muted>n/a</span>` : shard.builtRunsCount.toLocaleString()}</div>
              <div class=cell>${shard.commitsCount.toLocaleString()}</div>
              <div class=cell>${shard.minRun?.toLocaleString() ?? nothing}</div>
              <div class=cell>${shard.maxRun?.toLocaleString() ?? nothing}</div>
              <div class=cell><human-bytes bytes=${shard.sizeBytes}></human-bytes></div>
              <div class=cell><human-bytes bytes=${bytesPerRun(shard)}></human-bytes> / run</div>
              <div class=cell>${renderBytesPerTestResult(shard)}</div>
            `)}
          </section>
        `,
      })}
      </sl-dialog>
    `
  }

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    metadata: ([orgSlug, projectSlug]) => orgSlug && projectSlug ? pageMetadata({
      orgSlug, projectSlug, section: 'SuperAdm', accessGated: true,
    }) : undefined,
  });

  render() {
    if (!this._project)
      return nothing;

    return html`
      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${"superadm"}
      ></project-header>
      <app-body user-role=${this._project.access}>
        ${this._user?.isSuperUser ? html`
          <form-section label="Project Public ID">
            <sl-copy-button id=copy-button value=${this._project.projectPublicId}></sl-copy-button>
            <sl-tag size=medium>${this._project.projectPublicId}</sl-tag>
          </form-section>
          <form-section label="Report Index">
            ${this._reportIndexSize.render({
              pending: () => html`<span>Loading...</span>`,
              error: () => html`<span>failed to fetch</span>`,
              complete: ({ sizeBytes, downloadURL, shardsCount, testedCommitsCount, testsCount }) => html`
                <sl-tag size=medium><human-bytes bytes=${sizeBytes}></human-bytes></sl-tag>
                <sl-tag size=medium>${testsCount.toLocaleString()} tests</sl-tag>
                <sl-button size=small @click=${() => {
                  this._shardsDialog?.show();
                  this._shardDetails.run();
                }}>${shardsCount} shards</sl-button>
                <sl-tag size=medium>${testedCommitsCount} tested commits</sl-tag>
                <a href=${downloadURL} download="index.json.br"><sl-button size=small>Download</sl-button></a>
                <sl-button size=small variant=warning @click=${async () => {
                  if (!this.orgSlug || !this.projectSlug)
                    return;
                  await api.administration.rebuildReportIndex.GET({
                    orgSlug: this.orgSlug,
                    projectSlug: this.projectSlug,
                  });
                  this._reportIndexSize.run();
                }}>Rebuild Index</sl-button>
              `,
            })}
          </form-section>
          <form-section label="Shard Sizing">
            ${this._reportIndexSize.render({
              pending: () => html`<span>Loading...</span>`,
              error: () => html`<span>failed to fetch</span>`,
              complete: ({ shardBytesPerRun, shardTargetSizeBytes, shardTargetSizeOverridden, shardTargetRunCount, shardTargetRunCountMeasured }) => html`
                <sl-tag size=medium variant=${shardTargetSizeOverridden ? 'primary' : 'neutral'}>
                  ${`target ${humanReadableBytes(shardTargetSizeBytes)}${shardTargetSizeOverridden ? ' (override)' : ''}`}
                </sl-tag>
                <sl-tag size=medium>
                  ${shardBytesPerRun === null ? 'weight not measured yet' : `${humanReadableBytes(shardBytesPerRun)} / run`}
                </sl-tag>
                <sl-tag size=medium variant=${shardTargetRunCountMeasured ? 'success' : 'warning'}>
                  ${`${shardTargetRunCount.toLocaleString()} runs / shard${shardTargetRunCountMeasured ? '' : ' (from test count)'}`}
                </sl-tag>
              `,
            })}
          </form-section>
          <form-section label="Git JSON">
            ${this._repoSize.render({
              pending: () => html`<span>loading...</span>`,
              error: () => html`<span>failed to fetch</span>`,
              complete: ({ sizeBytes, downloadURL }) => html`
                <sl-tag size=medium><human-bytes bytes=${sizeBytes}></human-bytes></sl-tag>
                <a href=${downloadURL} download="repo.json.br"><sl-button size=small>Download</sl-button></a>
              `,
            })}
          </form-section>
          <form-section label="Advanced Settings" is-editor>
            <sl-checkbox ?checked=${this._project?.reportTimeIsUploadTime} @input=${async (event: SlInputEvent) => {
              if (!this.orgSlug || !this.projectSlug)
                return;
              await api.project.setProjectReportTimeAsUploadTimeForTest.POST({
                orgSlug: this.orgSlug,
                projectSlug: this.projectSlug,
                reportTimeAsUploadTime: (event.target as SlCheckbox).checked,
              });
              this.dispatchEvent(new FKProjectChanged({
                orgSlug: this.orgSlug,
                projectSlug: this.projectSlug,
              }));
            }}>Treat report time as upload time</sl-checkbox>
          </form-section>
          <form-section label="Git Repository">
            <p style="gap: var(--sl-spacing-2x-small);">
              <a-ext href=${forgeUtils.hasSourceUrl(this._project) ? forgeUtils.projectUrl(this._project) : nothing}>${this._project.sourceOwner}/${this._project.sourceRepo}</a-ext>
            </p>
            <p>
              ${this._renderLastFetchAttempt(this._project)}
            </p>
            <p>
              <sl-button @click=${async () => {
                if (!this.orgSlug || !this.projectSlug)
                  return;
                await api.administration.requestGitFetch.GET({
                  orgSlug: this.orgSlug,
                  projectSlug: this.projectSlug,
                });
                this._fetchedRepositoryStats.run();
              }}>Request Git Fetch</sl-button>
            </p>
          </form-section>
          ${this._renderShardsDialog()}
        ` : nothing}
      </app-body>
      <app-footer></app-footer>
    `
  }

  private _renderLastFetchAttempt(project: WireTypes.Project) {
    const { commits, branches, pullRequests } = this._fetchedRepositoryStats.value ?? {};
    if (project.sourceLastFetchHTTPCode === undefined || project.sourceLastFetchTime === undefined)
      return html`<span>fetching commits right now...</span>`;
    const relativeTime = html`
      <sl-tooltip>
        <sl-format-date slot="content" month="long" day="numeric" year="numeric" hour="numeric" minute="numeric" date=${new Date(project.sourceLastFetchTime).toISOString()}></sl-format-date>
        <b><sl-relative-time date=${new Date(project.sourceLastFetchTime).toISOString()}></sl-relative-time></b>
      </sl-tooltip>
    `;

    const provider = forgeUtils.providerName(project);
    const info = commits === undefined || branches === undefined || pullRequests === undefined ? nothing :
      html`Fetched <b>${commits}</b> commits, <b>${branches}</b> branches and <b>${pullRequests}</b> ${forgeUtils.pullRequestLabelPlural(project).toLowerCase()}.`;
    const attempt = (project.sourceLastFetchHTTPCode === 200) ?
      html`<b>Successfully</b> pulled history ${relativeTime}` :
      forgeUtils.isAccessDenied(project) ?
      html`${provider} <b>denied access</b> when we tried to pull history ${relativeTime}` :
      forgeUtils.isUnavailable(project) ?
      html`${provider} was <sl-tooltip content="${provider} responded with HTTP ${project.sourceLastFetchHTTPCode}"><b>temporarily unavailable</b></sl-tooltip> when we tried to pull history ${relativeTime}` :
      html`<sl-tooltip content="${provider} responded with HTTP ${project.sourceLastFetchHTTPCode}"><b>Failed</b></sl-tooltip> to pull history ${relativeTime}`
    return html`
      <v-box style="gap: var(--sl-spacing-2x-small);">
        <div>${info}</div>
        <div>${attempt}</div>
      </v-box>
    `
  }

  static styles = [linkStyles, githubMarkdownCSS, docLinkStyles, pageStyles, css`
    a-ext {
      color: rgb(9, 105, 218);
    }
    [user-role=viewer] {
      *:is([is-editor]) {
        display: none;
      }
    }


    #shards-dialog {
      --width: 80vw;

      section {
        display: grid;
        grid-template-columns: auto auto auto auto auto auto auto auto 1fr;
        font-size: var(--sl-font-size-small);

        .header {
          cursor: pointer;
          user-select: none;
          text-align: left;
          padding: var(--sl-spacing-x-small) var(--sl-spacing-small);
          border-bottom: 2px solid var(--sl-color-neutral-300);
          white-space: nowrap;
        }

        .cell {
          padding: var(--sl-spacing-x-small) var(--sl-spacing-small);
          border-bottom: 1px solid var(--sl-color-neutral-200);
        }

        .muted {
          color: var(--sl-color-neutral-500);
        }

        sl-icon {
          vertical-align: middle;
        }
      }
    }
  `];
}

// The shard's size describes the runs that were aggregated into it, so pair it
// with the run count recorded in the blob. Shards built before that was recorded
// fall back to the index's count.
function bytesPerRun(shard: { sizeBytes: number, runsCount: number, builtRunsCount: number|null }): number {
  const runs = shard.builtRunsCount ?? shard.runsCount;
  return runs ? shard.sizeBytes / runs : 0;
}

// Unlike runs, test results are only counted on the built blob, so shards built
// before that was recorded have no ratio.
function bytesPerTestResult(shard: { sizeBytes: number, builtTestResultsCount: number|null }): number|null {
  if (!shard.builtTestResultsCount)
    return null;
  return shard.sizeBytes / shard.builtTestResultsCount;
}

function renderBytesPerTestResult(shard: { sizeBytes: number, builtTestResultsCount: number|null }) {
  const ratio = bytesPerTestResult(shard);
  if (ratio === null)
    return html`<span class=muted>n/a</span>`;
  return `${ratio.toFixed(2)} B / test result`;
}
